import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import {
  Mic, FileText, Newspaper, Radio, ShoppingCart, Telescope, History, Plus,
  Target, Globe, Settings, Users, Activity, Shield, Search,
  ExternalLink, ChevronRight, RefreshCw, Sparkles, AlertTriangle, Check, PenLine,
  Loader2, Lock, Unlock, Zap, Link, Pencil, Calendar, Bell, Clock, Trash2, LayoutDashboard
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import AbsencesPanel from './AbsencesPanel';

// Composant TargetRow pour afficher une cible
const TargetRow = ({ target, sites, getSiteName, formatDate, formatTime, onEdit, onTake, onDelete }) => {
  const priorityIcon = target.priority === 'urgent' ? '🔴' : target.priority === 'low' ? '🟢' : '🟡';
  const isLate = target.is_late;
  const isVA = target.va;
  const isAging = target.is_aging;  // Cible de plus de 48h
  
  // Styles différenciés
  let rowStyle = 'bg-slate-800/30 border-slate-700/50';
  if (isVA) {
    rowStyle = 'bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-amber-500/20 border-amber-400/60 shadow-[0_0_10px_rgba(251,191,36,0.2)]';
  } else if (target.priority === 'urgent') {
    rowStyle = 'bg-red-950/20 border-red-700/40';
  } else if (isLate) {
    rowStyle = 'bg-slate-700/30 border-slate-500/40';
  }
  
  return (
    <div
      className={`
        flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all hover:bg-slate-800/50
        ${rowStyle}
      `}
    >
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-xs">{priorityIcon}</span>
        {isLate && (
          <span className="text-[8px] font-medium text-slate-400 bg-slate-600/50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
            +{target.report_count || 1}j
          </span>
        )}
        {isVA && (
          <span className="text-[8px] font-black text-amber-900 bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-300 px-1.5 py-0.5 rounded shadow-sm border border-amber-400/50">
            ⭐ VA
          </span>
        )}
        {isAging && !isVA && (
          <span 
            className="text-[8px] font-medium text-slate-500 bg-slate-700/40 px-1 py-0.5 rounded flex items-center gap-0.5"
            title="Cible proposée depuis plus de 48h"
          >
            <Clock className="w-2.5 h-2.5" />
            48h+
          </span>
        )}
      </div>
      {target.url && <ExternalLink className="w-3 h-3 text-sky-500 flex-shrink-0" />}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          {target.url && target.url.startsWith('http') ? (
            <a
              href={target.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-xs underline decoration-sky-500/40 hover:decoration-sky-400 hover:text-sky-400 font-medium cursor-pointer ${isVA ? 'text-amber-950' : 'text-sky-300'}`}
            >
              {target.title}
            </a>
          ) : (
            <span className={`text-xs font-medium ${isVA ? 'text-amber-950' : 'text-slate-200'}`}>{target.title}</span>
          )}
          {/* Sites demandés */}
          {target.sites && target.sites.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {target.sites.map(siteId => {
                const site = sites.find(s => s.id === siteId);
                return (
                  <span key={siteId} className="text-[8px] font-bold text-sky-300 bg-sky-900/60 border border-sky-700/50 px-1 py-0.5 rounded">
                    {site ? getSiteName(site) : siteId.slice(0, 4)}
                  </span>
                );
              })}
            </div>
          )}
          {target.categories && target.categories.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {target.categories.map(cat => (
                <span key={cat} className="text-[8px] font-medium text-violet-400 bg-violet-500/20 px-1 py-0.5 rounded uppercase">
                  {cat}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
          <Calendar className="w-2.5 h-2.5" />
          <span>{formatDate(target.created_at)} {formatTime(target.created_at)}</span>
          <span className="text-slate-600">·</span>
          <span>par {target.created_by?.name || 'Inconnu'}</span>
          {target.source === 'iOS Shortcut' && (
            <span className="text-fuchsia-400 bg-fuchsia-500/20 px-1 py-0.5 rounded font-medium">iOS</span>
          )}
          {target.source === 'PWA Share Target' && (
            <span className="text-teal-400 bg-teal-500/20 px-1 py-0.5 rounded font-medium">PWA</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(target); }}
          className="p-1 text-slate-500 hover:text-sky-400 transition-colors"
          title="Modifier"
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(target.id, target.title); }}
          className="p-1 text-slate-500 hover:text-red-400 transition-colors"
          title="Supprimer"
        >
          <Trash2 className="w-3 h-3" />
        </button>
        <Button
          size="sm"
          className={`h-6 text-[10px] px-3 font-semibold tracking-wider text-white shadow ${
            isVA 
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-amber-950 border border-amber-300/50' 
              : 'bg-sky-600 hover:bg-sky-500'
          }`}
          onClick={(e) => { e.stopPropagation(); onTake(target.id); }}
        >
          PRENDRE
        </Button>
      </div>
    </div>
  );
};

const CommandCenterNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [targets, setTargets] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMissionDialog, setShowMissionDialog] = useState(false);
  
  // État pour l'ajout rapide de cible
  const [showAddTargetDialog, setShowAddTargetDialog] = useState(false);
  const [newTargetTitle, setNewTargetTitle] = useState('');
  const [newTargetUrl, setNewTargetUrl] = useState('');
  const [newTargetPriority, setNewTargetPriority] = useState('normal');
  const [newTargetVa, setNewTargetVa] = useState(false);
  const [selectedSites, setSelectedSites] = useState([]);
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [submittingTarget, setSubmittingTarget] = useState(false);
  
  // État pour l'édition de cible
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editVa, setEditVa] = useState(false);
  const [editSites, setEditSites] = useState([]);
  
  // État du sémaphore de génération
  const [generationStatus, setGenerationStatus] = useState({
    locked: false,
    job_name: null,
    user_name: null,
    step: null,
    progress: null,
    started_at: null,
    current_site: null,
    total_sites: null,
    current_step: null,
    progress_percent: null
  });
  
  // États pour l'alerte horaire de production
  const [showHourlyAlert, setShowHourlyAlert] = useState(false);
  const [hourlyAlertData, setHourlyAlertData] = useState(null);
  const [hourlyAlertLoading, setHourlyAlertLoading] = useState(false);
  const [dismissingAlert, setDismissingAlert] = useState(false);
  const lastAlertCheckRef = useRef(null);
  
  // États pour la configuration des alertes (amiraux)
  const [showAlertConfigDialog, setShowAlertConfigDialog] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertConfigLoading, setAlertConfigLoading] = useState(false);
  const [savingAlertConfig, setSavingAlertConfig] = useState(false);
  
  // État pour les utilisateurs (panel absences)
  const [allUsers, setAllUsers] = useState([]);
  
  // Ref pour le polling interval (évite les re-renders en cascade)
  const pollIntervalRef = useRef(null);
  const currentPollRateRef = useRef(60000);
  const alertCheckIntervalRef = useRef(null);

  const isAmiral = user?.grade === 'amiral';
  const isCapitaine = user?.grade === 'capitaine' || isAmiral;

  // Load all data
  const loadData = useCallback(async () => {
    try {
      const [
        dashboardStatsRes,
        targetsRes,
        sitesRes,
        generationStatusRes,
        customSitesRes,
        conduiteSitesRes
      ] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/targets`).catch(() => ({ data: { targets: [] } })),
        axios.get(`${API}/wordpress-sites`).catch(() => ({ data: [] })),
        axios.get(`${API}/generation/status`).catch(() => ({ data: { locked: false } })),
        axios.get(`${API}/custom-sites`).catch(() => ({ data: { sites: [] } })),
        axios.get(`${API}/conduite/sites`).catch(() => ({ data: { groupe_sites: [], va_sites: [], autre_sites: [] } }))
      ]);

      const dashboardStats = dashboardStatsRes.data;
      
      // Récupérer les données de conduite avec les stats de publication
      const conduiteData = conduiteSitesRes.data;
      const conduiteSitesWithStats = [
        ...(conduiteData.groupe_sites || []),
        ...(conduiteData.va_sites || []),
        ...(conduiteData.autre_sites || [])
      ];
      
      // Créer un mapping des stats de conduite par site_id
      const conduiteStatsMap = {};
      conduiteSitesWithStats.forEach(s => {
        conduiteStatsMap[s.id] = {
          published_today: s.published_today || 0,
          draft_count: s.draft_count || 0,
          daily_objective: s.daily_objective || 2,
          objective_completed: s.objective_completed || false
        };
      });
      
      // Combiner sites WordPress et sites API custom, avec les stats de conduite
      const wpSites = (sitesRes.data || []).map(s => ({
        ...s,
        site_type: 'wordpress',
        // Enrichir avec les stats de conduite si disponibles
        ...(conduiteStatsMap[s.id] || {})
      }));
      const customSites = (customSitesRes.data.sites || []).map(s => ({ ...s, site_type: 'custom_api' }));
      const allSites = [...wpSites, ...customSites];
      
      setStats({
        interviews: dashboardStats.interviews || {},
        tribunes: dashboardStats.tribunes || {},
        ifrap: dashboardStats.ifrap || {},
        publications: dashboardStats.publications || {},
        veille: dashboardStats.veille || {},
        cp: dashboardStats.cp || {},
        tracking: dashboardStats.tracking,
        watchdog: dashboardStats.watchdog
      });
      setTargets(targetsRes.data.targets || []);
      setSites(allSites);
      setGenerationStatus(generationStatusRes.data || { locked: false });

    } catch (error) {
      console.error('Error loading command center data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling régulier - chargement initial uniquement
  useEffect(() => {
    loadData();
    // Setup initial polling at 60s
    pollIntervalRef.current = setInterval(loadData, 60000);
    currentPollRateRef.current = 60000;
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadData]);

  // Charger les utilisateurs pour le panel absences
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`);
        setAllUsers(res.data || []);
      } catch (error) {
        console.debug('Users load skipped (non-admin)');
      }
    };
    loadUsers();
  }, []);

  // Ajuster la fréquence de polling quand generationStatus.locked change
  useEffect(() => {
    const targetRate = generationStatus.locked ? 5000 : 60000;
    
    // Ne recréer l'intervalle que si le taux change réellement
    if (currentPollRateRef.current !== targetRate) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      pollIntervalRef.current = setInterval(loadData, targetRate);
      currentPollRateRef.current = targetRate;
    }
  }, [generationStatus.locked, loadData]);

  // ==================== ALERTE HORAIRE DE PRODUCTION ====================
  // Vérifie s'il y a une nouvelle alerte non vue et l'affiche automatiquement
  const checkHourlyAlert = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conduite/alerts/check-new`);
      if (res.data.has_new_alert) {
        // Nouvelle alerte non vue, charger les détails
        fetchHourlyAlertData();
      }
    } catch (error) {
      console.error('Error checking hourly alert:', error);
    }
  }, []);

  const fetchHourlyAlertData = async () => {
    setHourlyAlertLoading(true);
    try {
      const res = await axios.get(`${API}/conduite/alerts/hourly-status`);
      if (res.data && !res.data.already_seen) {
        setHourlyAlertData(res.data);
        setShowHourlyAlert(true);
      }
    } catch (error) {
      console.error('Error fetching hourly alert data:', error);
    } finally {
      setHourlyAlertLoading(false);
    }
  };

  const dismissHourlyAlert = async () => {
    if (!hourlyAlertData?.alert_id) return;
    setDismissingAlert(true);
    try {
      await axios.post(`${API}/conduite/alerts/mark-seen?alert_id=${hourlyAlertData.alert_id}`);
      setShowHourlyAlert(false);
      setHourlyAlertData(null);
      toast.success('Point horaire validé');
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setDismissingAlert(false);
    }
  };

  // Vérification initiale + polling toutes les minutes pour détecter le changement d'heure
  useEffect(() => {
    // Vérification initiale après un court délai (laisser le temps au chargement)
    const initialCheck = setTimeout(() => {
      checkHourlyAlert();
    }, 3000);

    // Polling toutes les 60 secondes pour détecter le changement d'heure
    alertCheckIntervalRef.current = setInterval(() => {
      const now = new Date();
      const currentHourKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}-${String(now.getUTCHours()).padStart(2, '0')}`;
      
      // Si on a changé d'heure depuis la dernière vérification, vérifier l'alerte
      if (lastAlertCheckRef.current !== currentHourKey) {
        lastAlertCheckRef.current = currentHourKey;
        checkHourlyAlert();
      }
    }, 60000); // Vérifier toutes les 60 secondes

    return () => {
      clearTimeout(initialCheck);
      if (alertCheckIntervalRef.current) {
        clearInterval(alertCheckIntervalRef.current);
      }
    };
  }, [checkHourlyAlert]);
  // ==================== FIN ALERTE HORAIRE ====================

  // ==================== CONFIGURATION ALERTES (AMIRAUX) ====================
  const loadAlertConfig = async () => {
    setAlertConfigLoading(true);
    try {
      const res = await axios.get(`${API}/conduite/alerts/config`);
      setAlertConfig(res.data.config);
    } catch (error) {
      console.error('Error loading alert config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setAlertConfigLoading(false);
    }
  };

  const saveAlertConfig = async (newConfig) => {
    setSavingAlertConfig(true);
    try {
      const res = await axios.put(`${API}/conduite/alerts/config`, newConfig);
      setAlertConfig(res.data.config);
      toast.success('Configuration sauvegardée');
      setShowAlertConfigDialog(false);
    } catch (error) {
      console.error('Error saving alert config:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingAlertConfig(false);
    }
  };

  const openAlertConfigDialog = () => {
    loadAlertConfig();
    setShowAlertConfigDialog(true);
  };
  // ==================== FIN CONFIG ALERTES ====================

  // Helper pour traduire les étapes de génération
  const getStepLabel = (step) => {
    const labels = {
      'preparing': 'Préparation',
      'queued': 'En attente',
      'analyzing': 'Analyse',
      'defining_angle': 'Définition angle',
      'ready_to_generate': 'Prêt',
      'generating_text': 'Génération texte',
      'generating_image': 'Génération image',
      'uploading': 'Upload',
      'finalizing': 'Finalisation',
      'completed': 'Terminé',
      'error': 'Erreur'
    };
    return labels[step] || step || 'En cours';
  };

  // Fonctions pour l'ajout rapide de cible
  const isUrl = (str) => {
    try {
      new URL(str);
      return str.startsWith('http://') || str.startsWith('https://');
    } catch {
      return false;
    }
  };

  const handleTargetInputChange = async (value) => {
    setNewTargetTitle(value);
    if (isUrl(value) && value.length > 15) {
      setScrapingUrl(true);
      try {
        const res = await axios.post(`${API}/targets/scrape-url`, { url: value });
        if (res.data.success && res.data.title) {
          setNewTargetTitle(res.data.title);
          setNewTargetUrl(res.data.url);
          toast.success('Titre récupéré automatiquement');
        }
      } catch (error) {
        setNewTargetUrl(value);
      } finally {
        setScrapingUrl(false);
      }
    } else if (!isUrl(value)) {
      setNewTargetUrl('');
    }
  };

  const handleAddTarget = async () => {
    if (!newTargetTitle.trim()) {
      toast.error('Veuillez saisir un sujet');
      return;
    }
    setSubmittingTarget(true);
    try {
      await axios.post(`${API}/targets`, {
        title: newTargetTitle.trim(),
        url: newTargetUrl || null,
        sites: selectedSites.length > 0 ? selectedSites : null,
        priority: newTargetPriority,
        va: newTargetVa,
      });
      toast.success('Cible ajoutée');
      setNewTargetTitle('');
      setNewTargetUrl('');
      setSelectedSites([]);
      setNewTargetPriority('normal');
      setNewTargetVa(false);
      setShowAddTargetDialog(false);
      loadData(); // Recharger les données
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSubmittingTarget(false);
    }
  };

  // Category counts for targets
  const categoryCounts = targets.reduce((acc, t) => {
    const cats = t.categories || ['autre'];
    cats.forEach(c => {
      acc[c] = (acc[c] || 0) + 1;
    });
    return acc;
  }, {});

  // Filter targets
  // Séparer: VA (max 2), Urgents non-VA (max 3), Autres (max 10)
  // Ordre chronologique: le plus récent en premier
  const availableTargets = targets
    .filter(t => !t.taken_by)
    .filter(t => selectedCategory === 'all' || (t.categories || []).includes(selectedCategory))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Plus récent en premier
  
  // VA targets (max 2)
  const vaTargets = availableTargets.filter(t => t.va).slice(0, 2);
  
  // Urgent non-VA targets (max 3)
  const urgentTargets = availableTargets
    .filter(t => !t.va && t.priority === 'urgent')
    .slice(0, 3);
  
  // Autres targets (ceux qui ne sont ni VA ni urgent) - max 10
  const vaIds = new Set(vaTargets.map(t => t.id));
  const urgentIds = new Set(urgentTargets.map(t => t.id));
  const otherTargets = availableTargets
    .filter(t => !vaIds.has(t.id) && !urgentIds.has(t.id))
    .slice(0, 10);
  
  // Total filtré pour l'affichage (on garde les groupes séparés pour le rendu)
  const filteredTargets = [...vaTargets, ...urgentTargets, ...otherTargets];

  // Site stats
  const siteStats = {
    connected: sites.filter(s => s.is_connected).length,
    notConfigured: sites.filter(s => !s.is_connected && !s.connection_error).length,
    disconnected: sites.filter(s => s.connection_error).length
  };

  const getSiteName = (site) => {
    if (!site.url) return site.name || 'Site';
    try {
      const hostname = new URL(site.url).hostname.replace('www.', '');
      return hostname.split('.')[0].toUpperCase();
    } catch {
      return site.name?.slice(0, 8) || 'Site';
    }
  };

  const handleTakeTarget = async (targetId) => {
    try {
      await axios.post(`${API}/targets/${targetId}/take`);
      toast.success('Cible réservée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  const handleDeleteTarget = async (targetId, title) => {
    try {
      await axios.delete(`${API}/targets/${targetId}`);
      // Mise à jour immédiate de l'état local (sans attendre le reload complet)
      setTargets(prev => prev.filter(t => t.id !== targetId));
      toast.success(`Cible "${title?.slice(0, 30)}..." supprimée`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' });
  };

  const openEditDialog = (target) => {
    setEditTarget(target);
    setEditTitle(target.title || '');
    setEditUrl(target.url || '');
    setEditPriority(target.priority || 'normal');
    setEditVa(target.va || false);
    setEditSites(target.sites || []);
    setShowEditDialog(true);
  };

  const handleEditTarget = async () => {
    if (!editTarget) return;
    setSubmittingTarget(true);
    try {
      await axios.put(`${API}/targets/${editTarget.id}`, {
        title: editTitle.trim(),
        url: editUrl.trim() || null,
        priority: editPriority,
        va: editVa,
        sites: editSites.length > 0 ? editSites : null,
      });
      toast.success('Cible modifiée');
      setShowEditDialog(false);
      setEditTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSubmittingTarget(false);
    }
  };

  // Tunnel Card Component - Tactical Navy Style
  const TunnelCard = ({ icon: Icon, title, stats: tunnelStats, link, badge, disabled, amiralOnly }) => {
    if (amiralOnly && !isAmiral) return null;
    if (disabled) return null;

    return (
      <div 
        className="bg-slate-900/70 border border-sky-950/80 hover:border-sky-500/50 rounded-lg p-2.5 cursor-pointer group transition-all shadow-sm"
        onClick={() => navigate(link)}
        data-testid={`tunnel-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-sky-950/40 border border-sky-800/30 rounded text-sky-400 group-hover:bg-sky-500/20 group-hover:text-sky-300 transition-colors flex-shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-200 truncate">{title}</span>
              {badge && (
                <span className="text-[8px] font-bold text-sky-300 bg-sky-900/40 border border-sky-600/50 px-1 py-0.5 rounded flex-shrink-0">
                  {badge}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-slate-500">
              {tunnelStats.map((stat, idx) => (
                <span key={idx} className="flex items-center gap-0.5">
                  <span>{stat.label}:</span>
                  <span className={`font-medium ${stat.highlight ? 'text-sky-400' : 'text-slate-300'}`}>
                    {stat.icon && <span className="mr-0.5">{stat.icon}</span>}
                    {stat.value}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-sky-400 flex-shrink-0" />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-sky-400 animate-spin" />
          <p className="text-slate-400">Chargement du centre de commande...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="command-center-new">
      {/* Header - Tactical Navy */}
      <div className="border-b border-sky-900/40 bg-slate-950/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="text-xl font-orbitron uppercase tracking-wider text-sky-400">
                Centre de Commande
              </div>
              <span className="text-[10px] text-sky-400/80 font-mono bg-sky-950/50 border border-sky-800/50 px-2 py-0.5 rounded">v2.0</span>
            </div>

            {/* Profile */}
            <div className="flex items-center gap-3">
              <div 
                className="flex items-center gap-2 pl-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate('/profile')}
                title="Voir mon profil"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-xs font-bold">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                </div>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-slate-200">{user?.full_name}</div>
                  <div className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded inline-block ${
                    isAmiral ? 'bg-sky-500/20 text-sky-400' : 
                    isCapitaine ? 'bg-blue-500/20 text-blue-400' : 
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {user?.grade || 'Utilisateur'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* Create Mission - Tactical Navy Style */}
        <div className="max-w-2xl mx-auto mb-6">
          <Card 
            className="bg-gradient-to-r from-sky-950/60 via-blue-900/40 to-sky-950/60 border-2 border-sky-500/40 hover:border-sky-400/80 shadow-[0_0_20px_rgba(56,189,248,0.15)] transition-all cursor-pointer"
            onClick={() => setShowMissionDialog(true)}
            data-testid="create-mission-card"
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-sky-500/20 rounded-lg border border-sky-500/30">
                  <Sparkles className="w-6 h-6 text-sky-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-sky-200 font-orbitron">Créer une Mission</h3>
                  <p className="text-xs text-slate-400">Nouvel article, Interview, Tribune, CP ou Veille</p>
                </div>
              </div>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md shadow-sky-600/30 text-sm px-6">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle Mission
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Barre de progression tricolore - Conduite du jour */}
        {(() => {
          // Calculer les totaux à partir des sites WordPress dans la conduite
          const conduiteSites = sites.filter(s => s.in_conduite && s.site_type === 'wordpress');
          if (conduiteSites.length === 0) return null;
          
          const totalPublished = conduiteSites.reduce((sum, s) => sum + (s.published_today || 0), 0);
          const totalDraft = conduiteSites.reduce((sum, s) => sum + (s.draft_count || 0), 0);
          const totalObjective = conduiteSites.reduce((sum, s) => sum + (s.daily_objective || 2), 0);
          const totalRemaining = Math.max(0, totalObjective - totalPublished - totalDraft);
          const completionPct = totalObjective > 0 ? Math.round((totalPublished / totalObjective) * 100) : 0;
          
          return (
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5 mb-6 flex items-center gap-4">
              <button 
                onClick={() => navigate('/conduite')}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 rounded-md text-sky-400 hover:text-sky-300 transition-colors"
                title="Accéder à la Conduite Éditoriale"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Conduite</span>
              </button>
              <div className="flex-1 h-2.5 bg-slate-800 rounded-full overflow-hidden flex">
                {totalPublished > 0 && (
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500" 
                    style={{ width: `${(totalPublished / totalObjective) * 100}%` }}
                    title={`${totalPublished} publiés`}
                  />
                )}
                {totalDraft > 0 && (
                  <div 
                    className="h-full bg-amber-500 transition-all duration-500" 
                    style={{ width: `${(totalDraft / totalObjective) * 100}%` }}
                    title={`${totalDraft} en cours`}
                  />
                )}
                {totalRemaining > 0 && (
                  <div 
                    className="h-full bg-red-500/70 transition-all duration-500" 
                    style={{ width: `${(totalRemaining / totalObjective) * 100}%` }}
                    title={`${totalRemaining} restants`}
                  />
                )}
              </div>
              <div className="flex items-center gap-3 text-[10px] whitespace-nowrap">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span className="text-emerald-400 font-medium">{totalPublished}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  <span className="text-amber-400 font-medium">{totalDraft}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500/70"></span>
                  <span className="text-red-400 font-medium">{totalRemaining}</span>
                </span>
                <span className="text-slate-500">/ {totalObjective}</span>
                <span className="text-sky-400 font-bold">{completionPct}%</span>
              </div>
            </div>
          );
        })()}

        {/* Encart Absences - en ligne sous la barre de progression */}
        <div className="mb-6 flex items-center gap-4 text-sm relative">
          <AbsencesPanel currentUser={user} users={allUsers} compact={true} />
        </div>

        {/* Cibles Section - Tactical Navy Style */}
        <div className="bg-slate-900/70 border border-sky-950/80 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-orbitron uppercase tracking-wider text-sky-400 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Cibles Proposées
              <span className="text-sm font-normal text-slate-400">({targets.filter(t => !t.taken_by).length})</span>
            </h2>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                onClick={() => setShowAddTargetDialog(true)}
                data-testid="add-target-btn"
              >
                <Plus className="w-3 h-3 mr-1" /> Ajouter
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-sky-900/60 text-slate-300 hover:border-sky-500 hover:text-sky-400"
                onClick={() => navigate('/cibles')}
              >
                Voir toutes <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>

          {/* Category Tabs removed */}

          {/* Targets List - Séparé en 3 sections: VA, Urgents, Autres */}
          <div className="space-y-3">
            {filteredTargets.length === 0 ? (
              <p className="text-center py-4 text-slate-500 text-sm">Aucune cible disponible</p>
            ) : (
              <>
                {/* Section VA (max 2) */}
                {vaTargets.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 px-1 pb-1">
                      <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">⭐ VA Prioritaires</span>
                      <span className="text-[9px] text-amber-500/60">({vaTargets.length})</span>
                    </div>
                    {vaTargets.map(target => (
                      <TargetRow 
                        key={target.id} 
                        target={target} 
                        sites={sites}
                        getSiteName={getSiteName}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        onEdit={openEditDialog}
                        onTake={handleTakeTarget}
                        onDelete={handleDeleteTarget}
                      />
                    ))}
                  </div>
                )}
                
                {/* Section Urgents non-VA (max 3) */}
                {urgentTargets.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 px-1 pb-1 border-t border-slate-700/30 pt-2">
                      <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">🔴 Urgents</span>
                      <span className="text-[9px] text-red-500/60">({urgentTargets.length})</span>
                    </div>
                    {urgentTargets.map(target => (
                      <TargetRow 
                        key={target.id} 
                        target={target} 
                        sites={sites}
                        getSiteName={getSiteName}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        onEdit={openEditDialog}
                        onTake={handleTakeTarget}
                        onDelete={handleDeleteTarget}
                      />
                    ))}
                  </div>
                )}
                
                {/* Section Autres (max 10) */}
                {otherTargets.length > 0 && (
                  <div className="space-y-1">
                    {(vaTargets.length > 0 || urgentTargets.length > 0) && (
                      <div className="flex items-center gap-2 px-1 pb-1 border-t border-slate-700/30 pt-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">📋 Autres cibles</span>
                        <span className="text-[9px] text-slate-500/60">({otherTargets.length})</span>
                      </div>
                    )}
                    {otherTargets.map(target => (
                      <TargetRow 
                        key={target.id} 
                        target={target} 
                        sites={sites}
                        getSiteName={getSiteName}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        onEdit={openEditDialog}
                        onTake={handleTakeTarget}
                        onDelete={handleDeleteTarget}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Legend & Distribution */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 pt-3 border-t border-slate-700/50 text-[10px] text-slate-500">
            <span className="flex items-center gap-1">🔴 Urgent</span>
            <span className="flex items-center gap-1">🟡 Normal</span>
            <span className="flex items-center gap-1">🟢 Basse</span>
            <span className="flex items-center gap-1">
              <span className="text-[8px] font-black text-amber-900 bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-300 px-1 rounded shadow-sm">⭐ VA</span> Prioritaire
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[8px] font-medium text-fuchsia-400 bg-fuchsia-500/20 px-1 rounded">iOS</span> Raccourci
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[8px] font-medium text-teal-400 bg-teal-500/20 px-1 rounded">PWA</span> Partage
            </span>
            {targets.filter(t => t.is_late && !t.taken_by).length > 0 && (
              <span className="flex items-center gap-1 text-slate-400">
                <span className="text-[8px] font-medium bg-slate-600/50 px-1 rounded">+Xj</span>
                {targets.filter(t => t.is_late && !t.taken_by).length} en attente
              </span>
            )}
            {targets.filter(t => t.taken_by).length > 0 && (
              <>
                <span className="text-slate-600">|</span>
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-violet-400" />
                  {Object.entries(
                    targets.filter(t => t.taken_by).reduce((acc, t) => {
                      const name = t.taken_by?.name || 'Inconnu';
                      acc[name] = (acc[name] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([name, count], idx) => (
                    <span key={name}>
                      {idx > 0 && ' · '}
                      <span className="text-violet-300">{name}</span>
                      <span className="font-bold text-violet-400 ml-0.5">{count}</span>
                    </span>
                  ))}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Tunnels Section - Compact single row */}
        <div className="flex flex-wrap gap-2 mb-6">

          {/* Interviews */}
          <TunnelCard
            icon={Mic}
            title="INTERVIEWS"
            link="/interviews"
            stats={[
              { label: 'En cours', value: stats.interviews?.in_progress || 0 },
              { label: '7j', value: stats.interviews?.published_7d || 0, highlight: true }
            ]}
          />

          {/* Tribunes */}
          <TunnelCard
            icon={FileText}
            title="TRIBUNES"
            link="/tribunes"
            disabled={!isCapitaine}
            stats={[
              { label: 'Semaine', value: stats.tribunes?.week_count || 0 },
              { label: 'IA', value: stats.tribunes?.ai_enabled ? 'ON' : 'OFF', icon: '●', highlight: stats.tribunes?.ai_enabled }
            ]}
          />

          {/* Auto-CP */}
          <TunnelCard
            icon={Newspaper}
            title="AUTO-CP"
            link="/communiques"
            disabled={!isCapitaine}
            stats={[
              { label: 'Attente', value: stats.cp?.pending || 0 },
              { label: '7j', value: stats.cp?.published_7d || 0, highlight: true }
            ]}
          />

          {/* IFRAP-API */}
          <TunnelCard
            icon={Radio}
            title="IFRAP"
            link="/tribunes"
            amiralOnly
            badge="AMIRAL"
            stats={[
              { label: '24h', value: stats.ifrap?.processed_24h || 0 },
              { label: 'Auto', value: stats.ifrap?.auto_enabled ? 'ON' : 'OFF', icon: '●', highlight: stats.ifrap?.auto_enabled }
            ]}
          />

          {/* Amazon */}
          <TunnelCard
            icon={ShoppingCart}
            title="AMAZON"
            link="/amazon"
            amiralOnly
            badge="AMIRAL"
            stats={[
              { label: '24h', value: 0 },
              { label: 'Sites', value: 2 }
            ]}
          />

          {/* Veille */}
          <TunnelCard
            icon={Telescope}
            title="VEILLE"
            link="/veille"
            stats={[
              { label: 'Sources', value: stats.veille?.sources_active || 0 },
              { label: 'Alertes', value: stats.veille?.alerts_unread || 0, highlight: (stats.veille?.alerts_unread || 0) > 0 }
            ]}
          />

          {/* Historique - Tactical Navy Style */}
          <div 
            className="bg-slate-900/70 border border-sky-950/80 hover:border-sky-500/50 rounded-lg p-2.5 cursor-pointer group transition-all shadow-sm"
            onClick={() => navigate('/publications')}
          >
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-sky-950/40 border border-sky-800/30 rounded text-sky-400 group-hover:bg-sky-500/20 group-hover:text-sky-300 transition-colors flex-shrink-0">
                <History className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-slate-200">HISTORIQUE</span>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  <span>Total: <span className="font-bold text-sky-400">{(stats.publications?.total || 0).toLocaleString()}</span></span>
                  <span>Mois: <span className="text-slate-300">{stats.publications?.this_month || 0}</span></span>
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-sky-400 flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* Bottom Section: Sites + Admin */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sites Connected - Tactical Navy */}
          <div className="lg:col-span-2 bg-slate-900/70 border border-sky-950/80 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-orbitron uppercase tracking-wider text-sky-400 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Sites Connectés
                <span className="text-xs font-normal text-slate-400">({sites.length})</span>
              </h2>
              {isAmiral && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-sky-900/60 text-slate-300 hover:border-sky-500 hover:text-sky-400"
                  onClick={() => navigate('/wordpress')}
                >
                  Gérer <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-6 sm:grid-cols-7 md:grid-cols-9 gap-1.5 mb-3">
              {sites.map(site => {
                const status = site.is_connected ? 'connected' : site.connection_error ? 'error' : 'pending';
                const statusDot = status === 'connected' ? '🟢' : status === 'error' ? '🔴' : '🟠';
                const isCustomApi = site.site_type === 'custom_api';
                
                return (
                  <div
                    key={site.id}
                    className={`bg-slate-900/60 border rounded px-1.5 py-1 text-center hover:bg-sky-950/30 transition-all cursor-pointer ${
                      isCustomApi 
                        ? 'border-fuchsia-500/40 hover:border-fuchsia-400/60' 
                        : 'border-sky-900/40 hover:border-sky-500/50'
                    }`}
                    onClick={() => window.open(site.url, '_blank')}
                    title={site.url}
                  >
                    <div className={`text-[8px] font-bold truncate ${isCustomApi ? 'text-fuchsia-200' : 'text-sky-200'}`}>
                      {isCustomApi ? site.short_name || site.name : getSiteName(site)}
                    </div>
                    <div className="text-[8px] leading-none flex items-center justify-center gap-0.5">
                      {statusDot}
                      {isCustomApi && <span className="text-fuchsia-400 text-[6px] font-bold">API</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-4 text-[10px] text-slate-500">
              <span className="flex items-center gap-1">🟢 Connecté ({siteStats.connected})</span>
              <span className="flex items-center gap-1">🟠 Non paramétré ({siteStats.notConfigured})</span>
              <span className="flex items-center gap-1">🔴 Déconnecté ({siteStats.disconnected})</span>
            </div>
          </div>

          {/* Administration (Amiral only) - Tactical Navy */}
          {isAmiral && (
            <div className="bg-slate-900/70 border border-sky-950/80 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-orbitron uppercase tracking-wider text-sky-300 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Administration
                </h2>
                <span className="text-[9px] font-bold text-sky-300 bg-sky-900/40 border border-sky-600/50 px-1.5 py-0.5 rounded">
                  AMIRAL
                </span>
              </div>

              <div className="space-y-3">
                {/* Tracking */}
                <div 
                  className="bg-slate-900/60 border border-sky-950/80 rounded-lg p-3 cursor-pointer hover:border-sky-600/40 transition-colors"
                  onClick={() => navigate('/admin/interview-tracking')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-300 flex items-center gap-2">
                      <Activity className="w-3.5 h-3.5 text-sky-400" />
                      Tracking Rédacteurs
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div className="text-[10px] text-slate-400 space-y-0.5">
                    <div>Inactifs: <span className={`${(stats.tracking?.inactifs_count || 0) > 0 ? 'text-amber-400' : 'text-slate-300'}`}>{stats.tracking?.inactifs_count || 0}/{stats.tracking?.total_redacteurs || 0}</span></div>
                    <div>Lancées 7j: <span className="text-slate-300">{stats.tracking?.started_7d || 0}</span></div>
                    <div>Publiées 7j: <span className="text-sky-400">{stats.tracking?.published_7d || 0}</span></div>
                  </div>
                </div>

                {/* Watchdog */}
                <div 
                  className="bg-slate-900/60 border border-sky-950/80 rounded-lg p-3 cursor-pointer hover:border-sky-600/40 transition-colors"
                  onClick={() => navigate('/admin/watchdog')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-300 flex items-center gap-2">
                      <AlertTriangle className={`w-3.5 h-3.5 ${stats.watchdog?.healthy ? 'text-emerald-400' : 'text-rose-400'}`} />
                      Watchdog
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div className="text-[10px] text-slate-400 flex items-center gap-2">
                    {stats.watchdog?.healthy ? (
                      <span className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-emerald-400" /> Healthy
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-rose-400">
                        <AlertTriangle className="w-3 h-3" /> {stats.watchdog?.stuck_jobs || 0} bloqué(s)
                      </span>
                    )}
                    <span>Jobs actifs: {stats.watchdog?.active_jobs || 0}</span>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-2 gap-2">
                  <div 
                    className="bg-slate-900/60 border border-sky-950/80 rounded-lg p-2 cursor-pointer hover:border-sky-600/40 transition-colors text-center"
                    onClick={() => navigate('/equipage')}
                  >
                    <Users className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <div className="text-[10px] text-slate-300">Utilisateurs</div>
                  </div>
                  <div 
                    className="bg-slate-900/60 border border-sky-950/80 rounded-lg p-2 cursor-pointer hover:border-sky-600/40 transition-colors text-center"
                    onClick={() => navigate('/profile')}
                  >
                    <Settings className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <div className="text-[10px] text-slate-300">Configuration</div>
                  </div>
                </div>
                
                {/* Configuration Alertes */}
                <div 
                  className="bg-amber-950/30 border border-amber-500/30 rounded-lg p-3 cursor-pointer hover:border-amber-500/50 transition-colors"
                  onClick={openAlertConfigDialog}
                  data-testid="alert-config-btn"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-amber-300 flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5" />
                      Configuration Alertes Horaires
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-amber-500/60" />
                  </div>
                  <div className="text-[10px] text-amber-200/60 mt-1">
                    Paramétrer les plages horaires et intervalles
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mission Dialog - Tactical Navy Design */}
      <Dialog open={showMissionDialog} onOpenChange={setShowMissionDialog}>
        <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] max-w-lg p-0 overflow-hidden rounded-xl backdrop-blur-2xl text-slate-100">
          <div className="bg-gradient-to-r from-sky-800/80 via-blue-900/70 to-sky-800/80 border-b border-sky-400/40 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-xl font-orbitron uppercase text-sky-400 tracking-wider flex items-center gap-3">
                <div className="p-2 bg-sky-500/20 rounded-lg border border-sky-500/30">
                  <Sparkles className="w-6 h-6" />
                </div>
                Nouvelle Mission
              </DialogTitle>
            </DialogHeader>
          </div>
          
          <div className="p-6 space-y-3">
            {/* Sémaphore de génération */}
            {generationStatus.locked ? (
              <div className="mb-4 p-4 bg-amber-950/50 border-2 border-amber-500/50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-500/30 rounded-lg animate-pulse">
                    <Lock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-amber-300 font-orbitron">Génération en cours</div>
                    <div className="text-xs text-amber-200/70">Nouvelle mission bloquée temporairement</div>
                  </div>
                </div>
                
                {/* Détails de la génération en cours */}
                <div className="bg-slate-900/60 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400">Mission :</span>
                    <span className="text-white font-medium truncate max-w-[200px]">{generationStatus.job_name || 'Sans nom'}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400">Par :</span>
                    <span className="text-sky-300">{generationStatus.user_name || 'Utilisateur'}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400">Étape :</span>
                    <span className="text-emerald-400">{getStepLabel(generationStatus.current_step)}</span>
                  </div>
                  {generationStatus.total_sites > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-400">Sites :</span>
                      <span className="text-white">{generationStatus.current_site || 0} / {generationStatus.total_sites}</span>
                    </div>
                  )}
                  
                  {/* Barre de progression */}
                  <div className="mt-2">
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 transition-all duration-500 rounded-full"
                        style={{ width: `${generationStatus.progress_percent || 0}%` }}
                      />
                    </div>
                    <div className="text-right text-[10px] text-slate-400 mt-1">
                      {generationStatus.progress_percent || 0}%
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-emerald-950/30 border border-emerald-500/30 rounded-xl flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Unlock className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-emerald-300">Système prêt</div>
                  <div className="text-[10px] text-emerald-200/60">Aucune génération en cours</div>
                </div>
              </div>
            )}
            
            {/* Nouvel Article - Primary action with better contrast */}
            <button
              onClick={() => { setShowMissionDialog(false); navigate('/create'); }}
              disabled={generationStatus.locked}
              className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all group shadow-lg border-2 ${
                generationStatus.locked 
                  ? 'bg-slate-800/50 border-slate-600/50 cursor-not-allowed opacity-60'
                  : 'bg-gradient-to-r from-sky-600/90 to-blue-700/90 hover:from-sky-500 hover:to-blue-600 border-sky-400/50 hover:border-sky-300'
              }`}
            >
              <div className={`p-3 rounded-lg transition-colors border ${
                generationStatus.locked 
                  ? 'bg-slate-700/50 border-slate-600/50' 
                  : 'bg-white/20 group-hover:bg-white/30 border-white/20'
              }`}>
                {generationStatus.locked ? (
                  <Loader2 className="w-7 h-7 text-slate-400 animate-spin" />
                ) : (
                  <PenLine className="w-7 h-7 text-white" />
                )}
              </div>
              <div className="text-left flex-1">
                <div className={`text-base font-semibold font-orbitron ${generationStatus.locked ? 'text-slate-400' : 'text-white'}`}>
                  Nouvel Article
                </div>
                <div className={`text-xs ${generationStatus.locked ? 'text-slate-500' : 'text-sky-100'}`}>
                  {generationStatus.locked ? 'Patientez...' : 'Rédiger un article manuellement'}
                </div>
              </div>
              <ChevronRight className={`w-5 h-5 transition-transform ${generationStatus.locked ? 'text-slate-500' : 'text-white group-hover:translate-x-1'}`} />
            </button>

            {/* Grille des autres options - Better contrast */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => { setShowMissionDialog(false); navigate('/interviews'); }}
                className="p-4 bg-slate-700/80 hover:bg-slate-600 border border-slate-500 hover:border-sky-400 rounded-xl text-center transition-all group"
              >
                <div className="p-2 bg-sky-500/30 rounded-lg mx-auto w-fit mb-2 group-hover:bg-sky-500/40 transition-colors">
                  <Mic className="w-6 h-6 text-sky-400" />
                </div>
                <div className="text-sm font-medium text-white">Interview</div>
                <div className="text-[10px] text-slate-300 mt-0.5">Auto-interview par email</div>
              </button>
              
              <button
                onClick={() => { setShowMissionDialog(false); navigate('/tribunes'); }}
                className={`p-4 bg-slate-700/80 hover:bg-slate-600 border border-slate-500 hover:border-sky-400 rounded-xl text-center transition-all group ${!isCapitaine ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!isCapitaine}
              >
                <div className="p-2 bg-sky-500/30 rounded-lg mx-auto w-fit mb-2 group-hover:bg-sky-500/40 transition-colors">
                  <FileText className="w-6 h-6 text-sky-400" />
                </div>
                <div className="text-sm font-medium text-white">Tribune</div>
                <div className="text-[10px] text-slate-300 mt-0.5">Programmer une tribune</div>
              </button>
              
              <button
                onClick={() => { setShowMissionDialog(false); navigate('/communiques'); }}
                className={`p-4 bg-slate-700/80 hover:bg-slate-600 border border-slate-500 hover:border-sky-400 rounded-xl text-center transition-all group ${!isCapitaine ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!isCapitaine}
              >
                <div className="p-2 bg-sky-500/30 rounded-lg mx-auto w-fit mb-2 group-hover:bg-sky-500/40 transition-colors">
                  <Newspaper className="w-6 h-6 text-sky-400" />
                </div>
                <div className="text-sm font-medium text-white">Communiqué</div>
                <div className="text-[10px] text-slate-300 mt-0.5">Créer un auto-CP</div>
              </button>
              
              <button
                onClick={() => { setShowMissionDialog(false); navigate('/veille'); }}
                className="p-4 bg-slate-700/80 hover:bg-slate-600 border border-slate-500 hover:border-sky-400 rounded-xl text-center transition-all group"
              >
                <div className="p-2 bg-sky-500/30 rounded-lg mx-auto w-fit mb-2 group-hover:bg-sky-500/40 transition-colors">
                  <Telescope className="w-6 h-6 text-sky-400" />
                </div>
                <div className="text-sm font-medium text-white">Veille</div>
                <div className="text-[10px] text-slate-300 mt-0.5">Configurer une veille</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog pour ajouter une cible - Identique à CiblesPage */}
      <Dialog open={showAddTargetDialog} onOpenChange={setShowAddTargetDialog}>
        <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] max-w-lg rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-sky-400 flex items-center gap-2 font-orbitron uppercase tracking-wider">
              <Plus className="w-5 h-5" />
              Nouvelle cible
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Proposez un sujet d'article à traiter par l'équipe
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                Sujet ou URL *
                {scrapingUrl && (
                  <span className="flex items-center gap-1 text-[10px] text-sky-400 normal-case font-normal">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Récupération du titre...
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  placeholder="Ex: https://... ou Crash boursier à Tokyo"
                  value={newTargetTitle}
                  onChange={(e) => handleTargetInputChange(e.target.value)}
                  className="bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-400 focus:ring-1 focus:ring-sky-400 pr-8"
                  data-testid="input-title"
                />
                {newTargetUrl && <Link className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />}
              </div>
              {newTargetUrl && (
                <p className="text-[10px] text-sky-400 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Lien attaché
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase">Priorité</Label>
              <RadioGroup value={newTargetPriority} onValueChange={setNewTargetPriority} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="urgent" id="cc-urgent" />
                  <Label htmlFor="cc-urgent" className="text-rose-400 cursor-pointer">🔴 Urgent</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="normal" id="cc-normal" />
                  <Label htmlFor="cc-normal" className="text-amber-400 cursor-pointer">🟡 Normal (défaut)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="cc-low" />
                  <Label htmlFor="cc-low" className="text-emerald-400 cursor-pointer">🟢 Basse</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center gap-3 py-2 px-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <input
                type="checkbox"
                id="cc-va-checkbox"
                checked={newTargetVa}
                onChange={(e) => setNewTargetVa(e.target.checked)}
                className="w-4 h-4 rounded border-amber-500 text-amber-500 focus:ring-amber-500 cursor-pointer"
              />
              <Label htmlFor="cc-va-checkbox" className="text-amber-400 cursor-pointer flex items-center gap-2">
                <span className="font-bold">VA</span>
                <span className="text-[10px] text-amber-300/70">(mise en exergue)</span>
              </Label>
            </div>

            {sites.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                  Sites demandés
                  <span className="text-[10px] text-slate-500 normal-case font-normal">(optionnel)</span>
                </Label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-slate-900 rounded border border-slate-700 max-h-32 overflow-y-auto">
                  {sites.map(site => {
                    const isSelected = selectedSites.includes(site.id);
                    let siteName = site.name || '';
                    if (site.url) {
                      try {
                        siteName = new URL(site.url).hostname.replace('www.', '').split('.')[0].toUpperCase();
                      } catch {}
                    }
                    return (
                      <button
                        key={site.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setSelectedSites(selectedSites.filter(id => id !== site.id));
                          } else {
                            setSelectedSites([...selectedSites, site.id]);
                          }
                        }}
                        className={`text-[10px] px-2 py-1 rounded transition-all font-semibold hover:opacity-80 ${
                          isSelected ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-800'
                        }`}
                      >
                        {siteName || site.id?.slice(0, 6)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTargetDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
              Annuler
            </Button>
            <Button
              onClick={handleAddTarget}
              disabled={submittingTarget || !newTargetTitle.trim()}
              className="bg-sky-600 hover:bg-sky-500 text-white font-semibold"
              data-testid="btn-confirm-add"
            >
              {submittingTarget ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifier une cible */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { if (!open) { setShowEditDialog(false); setEditTarget(null); } }}>
        <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] max-w-lg rounded-xl backdrop-blur-2xl text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-sky-400 flex items-center gap-2 font-orbitron uppercase tracking-wider text-sm">
              <Pencil className="w-4 h-4" />
              Modifier la cible
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Modifiez les informations de cette cible
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Titre *</Label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="bg-slate-900/80 border-slate-700 text-slate-100 focus:border-sky-400 text-sm"
                data-testid="cc-edit-title-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">URL source</Label>
              <Input
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://..."
                className="bg-slate-900/80 border-slate-700 text-slate-100 focus:border-sky-400 text-sm"
                data-testid="cc-edit-url-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Priorité</Label>
              <RadioGroup value={editPriority} onValueChange={setEditPriority} className="flex gap-3">
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="urgent" id="cc-edit-urgent" className="border-red-500 text-red-500" />
                  <Label htmlFor="cc-edit-urgent" className="text-red-400 cursor-pointer text-xs">🔴 Urgent</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="normal" id="cc-edit-normal" className="border-yellow-500 text-yellow-500" />
                  <Label htmlFor="cc-edit-normal" className="text-amber-400 cursor-pointer text-xs">🟡 Normal</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="low" id="cc-edit-low" className="border-green-500 text-green-500" />
                  <Label htmlFor="cc-edit-low" className="text-green-400 cursor-pointer text-xs">🟢 Basse</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center gap-3 p-2 rounded bg-slate-900/50 border border-slate-700">
              <button
                type="button"
                onClick={() => setEditVa(!editVa)}
                className={`w-10 h-5 rounded-full transition-colors relative ${editVa ? 'bg-amber-500' : 'bg-slate-700'}`}
                data-testid="cc-edit-va-toggle"
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${editVa ? 'left-5' : 'left-0.5'}`} />
              </button>
              <Label className={`text-xs font-semibold cursor-pointer ${editVa ? 'text-amber-400' : 'text-slate-400'}`} onClick={() => setEditVa(!editVa)}>
                ⭐ Valeur Ajoutée (VA)
              </Label>
            </div>

            {sites.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Sites cibles</Label>
                <div className="flex flex-wrap gap-1.5">
                  {sites.map(site => {
                    const isSelected = editSites.includes(site.id);
                    return (
                      <button
                        key={site.id}
                        type="button"
                        onClick={() => setEditSites(prev => isSelected ? prev.filter(id => id !== site.id) : [...prev, site.id])}
                        className={`text-[10px] px-2 py-1 rounded-md border transition-all font-medium ${
                          isSelected
                            ? 'bg-sky-500/20 border-sky-400/60 text-sky-300'
                            : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                        }`}
                        data-testid={`cc-edit-site-${site.id}`}
                      >
                        {getSiteName(site)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditTarget(null); }} className="border-slate-600 text-slate-300 hover:bg-slate-800">
              Annuler
            </Button>
            <Button
              onClick={handleEditTarget}
              disabled={submittingTarget || !editTitle.trim()}
              className="bg-sky-600 hover:bg-sky-500 text-white"
              data-testid="cc-edit-save-btn"
            >
              {submittingTarget ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Alerte Horaire de Production */}
      <Dialog open={showHourlyAlert} onOpenChange={() => {/* Ne pas fermer sans valider */}}>
        <DialogContent 
          className="bg-slate-950/98 border-2 border-amber-500/60 shadow-[0_0_60px_rgba(251,191,36,0.3)] max-w-2xl rounded-xl backdrop-blur-2xl text-slate-100"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="hourly-alert-dialog"
        >
          <DialogHeader className="border-b border-amber-500/30 pb-4">
            <DialogTitle className="text-amber-400 flex items-center gap-3 font-orbitron uppercase tracking-wider text-lg">
              <div className="p-2 bg-amber-500/20 rounded-lg border border-amber-500/40 animate-pulse">
                <Bell className="w-6 h-6" />
              </div>
              Point de Production Horaire
              <span className="ml-auto text-sm font-mono text-slate-400 normal-case">
                {hourlyAlertData?.hour !== undefined ? `${hourlyAlertData.hour}h00` : ''}
              </span>
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm mt-2">
              Prenez connaissance de l'avancement de la production pour tous les sites de la conduite.
            </DialogDescription>
          </DialogHeader>

          {hourlyAlertLoading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : hourlyAlertData ? (
            <div className="py-3 space-y-3 max-h-[70vh] overflow-y-auto">
              {/* Résumé global - Compact en ligne */}
              <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-900/60 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{hourlyAlertData.summary?.total_sites || 0} sites</span>
                  <span className="text-slate-600">|</span>
                  <span className="text-xs"><span className="text-emerald-400 font-bold">{hourlyAlertData.summary?.total_published || 0}</span> <span className="text-slate-500">pub.</span></span>
                  <span className="text-xs"><span className="text-amber-400 font-bold">{hourlyAlertData.summary?.total_draft || 0}</span> <span className="text-slate-500">brouil.</span></span>
                  <span className="text-xs"><span className="text-red-400 font-bold">{Math.max(0, (hourlyAlertData.summary?.total_objective || 0) - (hourlyAlertData.summary?.total_published || 0) - (hourlyAlertData.summary?.total_draft || 0))}</span> <span className="text-slate-500">restants</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">/{hourlyAlertData.summary?.total_objective || 0}</span>
                  <span className="text-sm font-bold text-sky-400">{hourlyAlertData.summary?.completion_percentage || 0}%</span>
                </div>
              </div>

              {/* Barre de progression compacte - 3 segments */}
              {(() => {
                const published = hourlyAlertData.summary?.total_published || 0;
                const draft = hourlyAlertData.summary?.total_draft || 0;
                const objective = hourlyAlertData.summary?.total_objective || 1;
                const unprepared = Math.max(0, objective - published - draft);
                const publishedPct = (published / objective) * 100;
                const draftPct = (draft / objective) * 100;
                const unpreparedPct = (unprepared / objective) * 100;
                return (
                  <div className="h-5 bg-slate-800 rounded-md overflow-hidden flex">
                    {published > 0 && (
                      <div className="h-full bg-emerald-500 flex items-center justify-center" style={{ width: `${publishedPct}%` }}>
                        <span className="text-[10px] font-bold text-white">{published}</span>
                      </div>
                    )}
                    {draft > 0 && (
                      <div className="h-full bg-amber-500 flex items-center justify-center" style={{ width: `${draftPct}%` }}>
                        <span className="text-[10px] font-bold text-white">{draft}</span>
                      </div>
                    )}
                    {unprepared > 0 && (
                      <div className="h-full bg-red-500/80 flex items-center justify-center" style={{ width: `${unpreparedPct}%` }}>
                        <span className="text-[10px] font-bold text-white">{unprepared}</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Sites en grille de carrés - Triés par catégorie: VA > Groupe > Autres */}
              {(() => {
                const allSites = hourlyAlertData.sites || [];
                // Séparer les sites stricts complétés
                const strictCompleted = allSites.filter(s => s.strict_objective && s.status === 'completed');
                const activeSites = allSites.filter(s => !(s.strict_objective && s.status === 'completed'));
                
                // Trier les sites actifs: VA > Groupe > Autres
                const categoryOrder = { 'va': 0, 'groupe': 1, 'autre': 2 };
                const sortedActive = [...activeSites].sort((a, b) => {
                  const catA = categoryOrder[a.category?.toLowerCase()] ?? 2;
                  const catB = categoryOrder[b.category?.toLowerCase()] ?? 2;
                  if (catA !== catB) return catA - catB;
                  // Puis par statut (en retard d'abord)
                  const statusOrder = { 'late': 0, 'behind': 1, 'on_track': 2, 'completed': 3 };
                  return (statusOrder[a.status] || 2) - (statusOrder[b.status] || 2);
                });

                const SiteCard = ({ site }) => {
                  const statusColors = {
                    'completed': 'bg-emerald-500/20 border-emerald-500/50',
                    'late': 'bg-red-500/20 border-red-500/50',
                    'behind': 'bg-amber-500/20 border-amber-500/50',
                    'on_track': 'bg-slate-800/80 border-slate-600/50'
                  };
                  const pctPublished = (site.published / site.daily_objective) * 100;
                  const pctDraft = (site.draft / site.daily_objective) * 100;
                  const pctRemaining = Math.max(0, 100 - pctPublished - pctDraft);
                  
                  return (
                    <div 
                      className={`relative p-2.5 rounded-lg border transition-all hover:scale-[1.02] ${statusColors[site.status] || statusColors.on_track}`}
                      title={`${site.name} - ${site.status_label}`}
                      data-testid={`alert-site-${site.id}`}
                    >
                      {/* Nom du site - complet */}
                      <div className="text-[11px] font-semibold text-slate-200 mb-2 leading-tight">{site.name}</div>
                      {/* Mini barre de progression - même logique que la barre globale */}
                      <div className="h-2 bg-slate-700/30 rounded-full overflow-hidden flex mb-1.5">
                        {site.published > 0 && (
                          <div className="h-full bg-emerald-500" style={{ width: `${pctPublished}%` }} />
                        )}
                        {site.draft > 0 && (
                          <div className="h-full bg-amber-500" style={{ width: `${pctDraft}%` }} />
                        )}
                        {pctRemaining > 0 && (
                          <div className="h-full bg-red-500/80" style={{ width: `${pctRemaining}%` }} />
                        )}
                      </div>
                      {/* Stats claires */}
                      <div className="flex items-center justify-between text-[9px]">
                        <div className="flex items-center gap-1.5">
                          {site.published > 0 && (
                            <span className="text-emerald-400 font-bold">{site.published} pub</span>
                          )}
                          {site.draft > 0 && (
                            <span className="text-amber-400 font-bold">{site.draft} brouil</span>
                          )}
                          {site.published === 0 && site.draft === 0 && (
                            <span className="text-slate-500">0 article</span>
                          )}
                        </div>
                        <span className="text-slate-400">obj: {site.daily_objective}</span>
                      </div>
                      {/* Statut si problème */}
                      {(site.status === 'late' || site.status === 'behind') && (
                        <div className={`text-[8px] text-center mt-1.5 py-0.5 rounded ${
                          site.status === 'late' ? 'bg-red-500/30 text-red-300' : 'bg-amber-500/30 text-amber-300'
                        }`}>
                          {site.status === 'late' ? '⚠ En retard' : '● À accélérer'}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="space-y-3">
                    {/* Sites actifs en grille */}
                    {sortedActive.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-3 h-3 text-slate-400" />
                          <span className="text-[10px] text-slate-400 uppercase tracking-wider">En cours ({sortedActive.length})</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {sortedActive.map((site, idx) => (
                            <SiteCard key={site.id || idx} site={site} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sites stricts complétés */}
                    {strictCompleted.length > 0 && (
                      <div className="pt-2 border-t border-slate-700/50">
                        <div className="flex items-center gap-2 mb-2">
                          <Check className="w-3 h-3 text-emerald-400" />
                          <span className="text-[10px] text-emerald-400/80 uppercase tracking-wider">Complétés ({strictCompleted.length})</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 opacity-70">
                          {strictCompleted.map((site, idx) => (
                            <SiteCard key={site.id || idx} site={site} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Indicateurs de warning compacts */}
              {(hourlyAlertData.summary?.sites_late > 0 || hourlyAlertData.summary?.sites_behind > 0) && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-950/30 border border-amber-500/30 rounded-lg">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  <span className="text-[10px] text-amber-300">
                    {hourlyAlertData.summary?.sites_late > 0 && `${hourlyAlertData.summary.sites_late} en retard`}
                    {hourlyAlertData.summary?.sites_late > 0 && hourlyAlertData.summary?.sites_behind > 0 && ' • '}
                    {hourlyAlertData.summary?.sites_behind > 0 && `${hourlyAlertData.summary.sites_behind} à accélérer`}
                  </span>
                </div>
              )}

              {/* Légende statuts */}
              <div className="flex items-center justify-center gap-4 text-[9px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500"></span>Publié</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-500"></span>Brouillon</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500"></span>Restant</span>
              </div>
            </div>
          ) : null}

          <DialogFooter className="border-t border-amber-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => {
                  dismissHourlyAlert();
                  navigate('/conduite');
                }}
              >
                <Clock className="w-4 h-4 mr-2" />
                Voir la conduite
              </Button>
              <Button
                onClick={dismissHourlyAlert}
                disabled={dismissingAlert}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold"
                data-testid="hourly-alert-dismiss-btn"
              >
                {dismissingAlert ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                J'ai pris connaissance
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Configuration Alertes (Amiraux) */}
      <Dialog open={showAlertConfigDialog} onOpenChange={setShowAlertConfigDialog}>
        <DialogContent 
          className="bg-slate-950/98 border-2 border-amber-500/50 shadow-[0_0_50px_rgba(251,191,36,0.2)] max-w-md rounded-xl backdrop-blur-2xl text-slate-100"
          data-testid="alert-config-dialog"
        >
          <DialogHeader className="border-b border-amber-500/30 pb-4">
            <DialogTitle className="text-amber-400 flex items-center gap-3 font-orbitron uppercase tracking-wider text-lg">
              <div className="p-2 bg-amber-500/20 rounded-lg border border-amber-500/40">
                <Settings className="w-5 h-5" />
              </div>
              Configuration des Alertes
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm mt-2">
              Paramétrez les alertes de production pour tous les utilisateurs
            </DialogDescription>
          </DialogHeader>

          {alertConfigLoading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : alertConfig ? (
            <div className="py-4 space-y-5">
              {/* Activation générale */}
              <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-slate-200">Alertes activées</div>
                  <div className="text-xs text-slate-400 mt-0.5">Afficher les alertes de production</div>
                </div>
                <button
                  onClick={() => setAlertConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    alertConfig.enabled ? 'bg-emerald-600' : 'bg-slate-700'
                  }`}
                  data-testid="alert-toggle-enabled"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    alertConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Plage horaire */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-slate-200">Plage horaire</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Début</label>
                    <select
                      value={alertConfig.start_hour}
                      onChange={(e) => setAlertConfig(prev => ({ ...prev, start_hour: parseInt(e.target.value) }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid="alert-start-hour"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-slate-500 pt-5">→</div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-400 mb-1 block">Fin</label>
                    <select
                      value={alertConfig.end_hour}
                      onChange={(e) => setAlertConfig(prev => ({ ...prev, end_hour: parseInt(e.target.value) }))}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
                      data-testid="alert-end-hour"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Les alertes n'apparaîtront qu'entre ces heures (heure Paris)
                </div>
              </div>

              {/* Week-end */}
              <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-slate-200">Alertes le week-end</div>
                  <div className="text-xs text-slate-400 mt-0.5">Samedi et dimanche inclus</div>
                </div>
                <button
                  onClick={() => setAlertConfig(prev => ({ ...prev, weekend_enabled: !prev.weekend_enabled }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    alertConfig.weekend_enabled ? 'bg-emerald-600' : 'bg-slate-700'
                  }`}
                  data-testid="alert-toggle-weekend"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    alertConfig.weekend_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Intervalle */}
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-slate-200">Intervalle d'apparition</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 60, label: '1h' },
                    { value: 120, label: '2h' },
                    { value: 180, label: '3h' },
                    { value: 240, label: '4h' }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setAlertConfig(prev => ({ ...prev, interval_minutes: opt.value }))}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        alertConfig.interval_minutes === opt.value
                          ? 'bg-amber-600 text-white border-2 border-amber-400'
                          : 'bg-slate-800 text-slate-300 border border-slate-600 hover:border-slate-500'
                      }`}
                      data-testid={`alert-interval-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Fréquence des rappels pour chaque utilisateur
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="border-t border-amber-500/30 pt-4">
            <div className="flex items-center gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowAlertConfigDialog(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={() => saveAlertConfig(alertConfig)}
                disabled={savingAlertConfig || !alertConfig}
                className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-semibold"
                data-testid="alert-config-save-btn"
              >
                {savingAlertConfig ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommandCenterNew;
